import { Anchor, Breadcrumbs, Card, Grid, Text } from '@mantine/core';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import GuidanceModal from '../../components/UserFlows/guidanceModal';
import ViberMessageForm from '../../components/ViberMessages/ViberMessageForm';
import algorithms from '../../constants/Algorithms';
import { Page } from '../../constants/pageTitles';
import recommenderRules from '../../constants/recommenderRules';
import useFetch from '../../helpers/useFetch';

const page = `${Page['viberMessage']}`;

const ViberMessagesNewPage = () => {
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();

  const [viberMessage, setViberMessage] = useState();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [guidanceShow, setGuidanceShow] = useState(false);

  const { accountId, domainId } = useParams();

  useEffect(() => {
    const from = queryString.parse(location.search).from;
    if (from) {
      fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages/${from}`)
        .then(({ viberMessage }) => {
          viberMessage.name = `Copy of ${viberMessage.name}`;
          delete viberMessage.id;
          setViberMessage(viberMessage);
        })
        .catch(setLoadError);
    } else {
      setViberMessage({
        name: 'New Viber message',
        message: '',
        mode: 'text',
        type: 'message',
        recommenderConfig: {
          rules: recommenderRules,
          algorithm: Object.keys(algorithms).find(
            (algorithm) =>
              algorithms[algorithm].allowedScopes === undefined ||
              algorithms[algorithm].allowedScopes.includes('viberMessage')
          ),
        },
        isPromotional: true,
        btnUrl: '',
        imageUrl: '',
        btnText: '',
        onDailyLimitReached: 'reschedule',
        maxReschedules: 3,
      });
    }
  }, [accountId, domainId, fetch, location.search]);

  const create = async (viberMessage, options = {}) => {
    // Once the message has an id (e.g. after a combined "Save & submit"), keep updating it rather than
    // creating duplicates on subsequent saves.
    const hasId = !!viberMessage.id;
    try {
      const { viberMessage: saved } = await fetch(
        hasId
          ? `/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages/${viberMessage.id}`
          : `/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages`,
        {
          method: hasId ? 'PUT' : 'POST',
          body: JSON.stringify(viberMessage),
        }
      );
      if (!options.skipPostSave) {
        setViberMessage(saved);
        setGuidanceShow(true);
      }
      return saved;
    } catch (e) {
      if (options.skipPostSave) {
        throw e;
      }
      setUserError(e);
      return undefined;
    }
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!viberMessage) {
    return <Spinner />;
  }

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: `#/accounts/${accountId}/domains/${domainId}/viber` },
    { title: 'New' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | New`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>
      <GuidanceModal
        type="Viber"
        redirect={`/accounts/${accountId}/domains/${domainId}/viber`}
        show={guidanceShow}
        elementTypes={['workflows']}
        element={viberMessage}
        setShow={() => setGuidanceShow(false)}
        newElement={true}
      />
      <Grid>
        <Grid.Col span={12}>
          <Card withBorder>
            <ViberMessageForm
              viberMessage={viberMessage}
              saveViberMessage={(viberMessage, options) => create(viberMessage, options)}
              allowSaveAndSubmit
              onBack={() => navigate(-1)}
            />
          </Card>
        </Grid.Col>
      </Grid>
      {userError ? (
        <Grid>
          <Grid.Col span={12}>
            <div style={{ textAlign: 'right' }}>
              <ErrorMessage message={userError.message} />
            </div>
          </Grid.Col>
        </Grid>
      ) : null}
    </>
  );
};

export default ViberMessagesNewPage;
